import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { loadGuidance, recordEngagement, ENGAGEMENT_EVENTS } from '../../services/training';
import safeHtml from '../../helpers/safeHtml';
import SupportNotice from '../Report/SupportNotice';
import './GuidanceArticle.css';

/**
 * Reads one guidance article.
 *
 * This exists so an assessment result can point at the part of a page that
 * answers it. The library used to live on an external site, and a flagged
 * result could only say "go to that site and find the Mismanagement section of
 * the Financial Wellbeing page" in prose. Every section here carries its slug
 * as an anchor, so #financial-mismanagement lands on it and the reader does not
 * have to go hunting.
 *
 * Bodies are authored HTML and are sanitised before they are rendered.
 */
function GuidanceArticle() {
  const { articleSlug } = useParams();
  const location = useLocation();

  const [article, setArticle] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // Guards the SHOWN event so it is recorded once per article, not once per
  // render and not again when the hash changes.
  const shownFor = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setArticle(null);

    loadGuidance(articleSlug)
      .then((loaded) => {
        if (cancelled) return;
        setArticle(loaded);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || 'That guidance could not be loaded.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [articleSlug]);

  useEffect(() => {
    if (!article || shownFor.current === article.articleId) return;
    shownFor.current = article.articleId;
    recordEngagement({ articleId: article.articleId, event: ENGAGEMENT_EVENTS.SHOWN });
  }, [article]);

  // The anchor is handled here rather than left to the browser: the content
  // arrives after the first paint, so by the time the section exists the
  // browser has already given up on the hash.
  const hash = location.hash ? location.hash.replace(/^#/, '') : '';
  useEffect(() => {
    if (!article || !hash) return;
    const target = document.getElementById(hash);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const section = article.sections.filter((item) => item.slug === hash)[0];
    if (section) {
      recordEngagement({
        articleId: article.articleId,
        sectionId: section.sectionId,
        event: ENGAGEMENT_EVENTS.OPENED,
      });
    }
  }, [article, hash]);

  const jumpTo = useCallback((slug) => {
    const target = document.getElementById(slug);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (loading) {
    return (
      <div className="guidance-wrap">
        <p className="guidance-status">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="guidance-wrap">
        <div className="guidance-error" role="alert">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="guidance-wrap">
      <article className="guidance-article">
        <header className="guidance-header">
          <h1 className="guidance-title">{article.title}</h1>
          {article.summary ? <p className="guidance-summary">{article.summary}</p> : null}
        </header>

        {/* Worth having on these pages: several run past twenty sections, and
            the point of the import was that a reader can get to one directly. */}
        {article.sections.length > 1 ? (
          <nav className="guidance-contents" aria-label="On this page">
            <p className="guidance-contents-label">On this page</p>
            <ul>
              {article.sections.map((section) => (
                <li key={section.slug}>
                  <button type="button" onClick={() => jumpTo(section.slug)}>
                    {section.heading}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ) : null}

        {article.sections.map((section) => (
          <section
            key={section.slug}
            id={section.slug}
            className={
              'guidance-section' + (hash === section.slug ? ' guidance-section-targeted' : '')
            }
          >
            {section.heading ? <h2 className="guidance-heading">{section.heading}</h2> : null}
            <div className="guidance-body" dangerouslySetInnerHTML={safeHtml(section.body)} />
          </section>
        ))}
        {/* Guidance is where people read about their results alone, so crisis help is
            always on the page, not only on the result screen. */}
        <SupportNotice compact crisisOnly />
      </article>
    </div>
  );
}

export default GuidanceArticle;
